import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../components/Button.css";
import "./ResidentRegister.css";
import { TOWERS, UNIT_TYPES, FLOORS } from "../../constants/towers";

export default function ResidentRegister() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    tower: "",
    unitType: "",
    floor: "",
    residentType: "",
    phone: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_API_URL;

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (loading) return;

    setError("");
    setSuccess("");

    if (!form.tower || !form.unitType || !form.floor || !form.residentType || !form.phone) {
      setError("Please complete all fields, including tower, unit type, floor, owner/tenant, and phone");
      return;
    }

    // Basic client-side validation
    if (!form.email || !form.email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    const unitNumber = `${form.unitType}-${form.floor}`;

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, unitNumber }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Registration failed");
      }

      setSuccess("Registration successful. Please check your email to verify. After verification, wait for Tower Admin approval.");
      setTimeout(() => navigate("/resident/pending"), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-shell">
      <div className="register-card">
        <div className="register-title">New Resident Registration</div>
        <div className="register-subtitle">
          Create your account to access the resident portal.
        </div>

        {error && <div className="register-error">{error}</div>}
        {success && <div className="register-success">{success}</div>}

        <form onSubmit={handleSubmit} className="register-form">
          <label className="register-label">
            Full name
            <input
              name="name"
              className="register-input"
              value={form.name}
              onChange={handleChange}
              required
            />
          </label>

          <label className="register-label">
            Email
            <input
              type="email"
              name="email"
              className="register-input"
              value={form.email}
              onChange={handleChange}
              required
            />
          </label>

          <label className="register-label">
            Password
            <input
              type="password"
              name="password"
              className="register-input"
              value={form.password}
              onChange={handleChange}
              required
            />
          </label>

          <label className="register-label">
            Owner or Tenant
            <select
              name="residentType"
              className="register-input"
              value={form.residentType}
              onChange={handleChange}
              required
            >
              <option value="">Select</option>
              <option value="Owner">Owner</option>
              <option value="Tenant">Tenant</option>
            </select>
          </label>

          <div className="register-section-header">Tower</div>

          <label className="register-label">
            Tower
            <select
              name="tower"
              className="register-input"
              value={form.tower}
              onChange={handleChange}
              required
            >
              <option value="">Select tower</option>
              {TOWERS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>

          <label className="register-label">
            Unit Type
            <select
              name="unitType"
              className="register-input"
              value={form.unitType}
              onChange={handleChange}
              required
            >
              <option value="">Select unit type</option>
              {UNIT_TYPES.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </label>

          <label className="register-label">
            Floor
            <select
              name="floor"
              className="register-input"
              value={form.floor}
              onChange={handleChange}
              required
            >
              <option value="">Select floor</option>
              {FLOORS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </label>

          <label className="register-label">
            Phone
            <input
              name="phone"
              className="register-input"
              value={form.phone}
              onChange={handleChange}
              required
            />
          </label>

          <button
            type="submit"
            className="register-button"
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <div className="register-footer">
          <p className="register-info">
            ℹ️ <strong>Note:</strong> Each unit can only have one active registration. If you've already registered, check your email for verification link.
          </p>
        </div>
      </div>
    </div>
  );
}